import instanceJWT from "./apiJwt";
import type { ClientContext } from "../models/eplex/ClientContext";

/**
 * Контекст клиента отдаёт бэкенд ЛК — он же зовёт LH напрямую
 * ({{action=registration_profile_bundle_get}}, заголовок {{X-Internal-Secret}}) и
 * держит кэш (CAB-672, CAB-643). Через прокси к router это НЕ идёт: прокси нужен
 * там, где обращается браузер, а у него сервисного секрета быть не может.
 *
 * Поэтому путь без префикса `eplex/` — как у `profile/settings`, это наш бэкенд,
 * а не проксируемый Eplex API.
 */
const ENDPOINT = "profile/context";
const TRIAL_EXIT_ENDPOINT = "profile/trial-exit";

/** Признак пробного режима: бэк отдаёт и булево, и 0/1. */
/** Флаг с бэка: приходит и булевым, и числом 0/1. */
type FlagLike = boolean | number | null;

interface ContextEnvelope {
  status?: boolean;
  data?: {
    client_id?: number | null;
    profile_id?: number | null;
    name?: string | null;
    client_name?: string | null;
    trial?: FlagLike;
    demo_client_id?: number | null;
    demo_profile_id?: number | null;
    partner_id?: number | null;
    profile_blocked?: FlagLike;
    client_blocked?: FlagLike;
    eplex_blocked?: FlagLike;
    client_active?: FlagLike;
    signal_hash?: string | null;
    email?: string | null;
    login?: string | null;
  };
}

/**
 * Булевы в связке LH настоящие булевы, но по пути через PHP они легко становятся
 * 0/1 — приводим сами, чтобы `0` не оказался истиной в интерфейсе.
 */
function toBool(value: boolean | number | null | undefined): boolean {
  return value === true || value === 1;
}

export async function fetchClientContext(): Promise<ClientContext> {
  const response = await instanceJWT.get<ContextEnvelope>(ENDPOINT);
  const data = response.data?.data ?? {};

  return {
    clientId: data.client_id ?? null,
    profileId: data.profile_id ?? null,
    name: data.name ?? "",
    clientName: data.client_name ?? "",
    trial: toBool(data.trial),
    demoClientId: data.demo_client_id ?? null,
    demoProfileId: data.demo_profile_id ?? null,
    partnerId: data.partner_id ?? null,
    profileBlocked: toBool(data.profile_blocked),
    clientBlocked: toBool(data.client_blocked),
    eplexBlocked: toBool(data.eplex_blocked),
    clientActive: toBool(data.client_active),
    signalHash: typeof data.signal_hash === "string" && data.signal_hash !== "" ? data.signal_hash : null,
    email: data.email ?? null,
    login: data.login ?? null,
  };
}

/**
 * Переход в рабочий режим (CAB-568). Необратимо: LH снимает {{trial}} экшном
 * {{client_trial_exit}}, второй раз он отвечает 409.
 *
 * Зовёт бэкенд ЛК, а не браузер: экшн закрыт сервисным секретом, которого у
 * браузера быть не может.
 *
 * 409 наверх пробрасываем как есть — вызывающая сторона трактует его не как
 * ошибку, а как «уже перешли» (гонка двух вкладок).
 */
export async function exitTrial(): Promise<void> {
  await instanceJWT.post(TRIAL_EXIT_ENDPOINT);
}

/**
 * Выход из Eplex (CAB-641). Куки гасит бэкенд: eplex_refresh с HttpOnly фронту
 * недоступен физически.
 *
 * Кука одна на домен .eplex.ru, поэтому это выход из ВСЕХ продуктов Eplex в этом
 * браузере — подпись кнопки обязана это обещать.
 */
export async function unifiedLogout(): Promise<void> {
  await instanceJWT.post("auth/unified/logout");
}

/**
 * Завершение сеансов на всех устройствах. Это НЕ то же, что выход: здесь отзыв —
 * двигается метка профиля в LH, и все ранее выданные токены становятся
 * недействительны разом, на любом устройстве.
 */
export async function unifiedLogoutAll(): Promise<void> {
  await instanceJWT.post("auth/unified/logout-all");
}
